import React, { useState } from 'react';
import { Button, Content, Flex, FlexItem, HelperText, HelperTextItem, TextInput } from '@patternfly/react-core';
import { ActionsColumn, type IAction, Td, Tr } from '@patternfly/react-table';
import type { ProductCategory } from '../../../types/models/ProductCategory';

// Names are unique, required, ≤ 64 chars (backend ProductCategoryRequest). Validated client-side to
// disable Save early; the backend re-validates (duplicate/length) and surfaces its error in data.name.
const NAME_MAX = 64;

export interface CategoryMutationResult {
  ok: boolean;
  /** Backend field-level error (data.name), surfaced inline; null for non-field failures. */
  fieldError: string | null;
}

interface ProductCategoryRowProps {
  category: ProductCategory;
  /** Number of products currently in this category (from the grid); blocks delete when > 0. */
  usageCount: number;
  onRename: (id: number, name: string) => Promise<CategoryMutationResult>;
  /** DELETE the category; the parent refetch drops this row on success. */
  onDelete: (id: number) => Promise<{ ok: boolean }>;
}

function ProductCategoryRow({ category, usageCount, onRename, onDelete }: ProductCategoryRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const trimmed = draft.trim();
  const isNameValid = trimmed.length > 0 && trimmed.length <= NAME_MAX;
  const isUnchanged = trimmed === category.name;
  const helperError = serverError ?? (trimmed.length > NAME_MAX ? `Name must be at most ${NAME_MAX} characters.` : null);

  const inputId = `category-name-input-${category.id}`;

  // A category can only be deleted once nothing references it — the backend rejects otherwise. Pre-disable
  // with the reason (focusable tooltip via isAriaDisabled) so the user reassigns first.
  const deleteDisabledReason =
    usageCount > 0
      ? `Used by ${usageCount} product${usageCount === 1 ? '' : 's'}; reassign them before deleting it.`
      : null;

  const openEditor = () => {
    setDraft(category.name);
    setServerError(null);
    setIsEditing(true);
  };

  const cancelEditor = () => {
    setIsEditing(false);
  };

  const save = async () => {
    // Renaming to the same name is a no-op — just close the editor (avoids a wasted PATCH and a
    // misleading "renamed" notification). Mirrors EditProductModal's isUnchanged guard.
    if (isUnchanged) {
      setIsEditing(false);
      return;
    }
    if (!isNameValid) {
      return;
    }
    setIsSaving(true);
    setServerError(null);
    const result = await onRename(category.id, trimmed);
    setIsSaving(false);
    if (result.ok) {
      setIsEditing(false);
    } else {
      setServerError(result.fieldError);
    }
  };

  const remove = async () => {
    setIsDeleting(true);
    await onDelete(category.id);
    // On success the parent refetch unmounts this row; on failure it stays (the drawer notification
    // explains why). Clearing the flag is a no-op if already unmounted.
    setIsDeleting(false);
  };

  const rowActions: IAction[] = [
    {
      title: 'Rename',
      onClick: openEditor,
      isAriaDisabled: isSaving || isDeleting,
    },
    {
      title: 'Delete',
      onClick: () => void remove(),
      isDanger: true,
      isAriaDisabled: deleteDisabledReason !== null || isSaving || isDeleting,
      ...(deleteDisabledReason ? { tooltipProps: { content: deleteDisabledReason } } : {}),
    },
  ];

  return (
    <Tr>
      <Td dataLabel="Category">
        {isEditing ? (
          <Flex
            spaceItems={{ default: 'spaceItemsSm' }}
            alignItems={{ default: 'alignItemsFlexStart' }}
            flexWrap={{ default: 'nowrap' }}
          >
            <FlexItem>
              <TextInput
                id={inputId}
                type="text"
                value={draft}
                onChange={(_event, value) => {
                  setDraft(value);
                  if (serverError) {
                    setServerError(null);
                  }
                }}
                validated={helperError ? 'error' : 'default'}
                isDisabled={isSaving}
                aria-label={`Name for category ${category.name}`}
              />
              {helperError && (
                <HelperText>
                  <HelperTextItem variant="error">{helperError}</HelperTextItem>
                </HelperText>
              )}
            </FlexItem>
            <FlexItem>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void save()}
                isLoading={isSaving}
                isDisabled={isSaving || !isNameValid || isUnchanged}
              >
                Save
              </Button>
            </FlexItem>
            <FlexItem>
              <Button variant="link" size="sm" onClick={cancelEditor} isDisabled={isSaving}>
                Cancel
              </Button>
            </FlexItem>
          </Flex>
        ) : (
          category.name
        )}
      </Td>
      <Td dataLabel="Usage">
        {usageCount > 0 ? (
          `${usageCount} product${usageCount === 1 ? '' : 's'}`
        ) : (
          <Content component="small">Unused</Content>
        )}
      </Td>
      <Td isActionCell>{!isEditing && <ActionsColumn items={rowActions} />}</Td>
    </Tr>
  );
}

export default ProductCategoryRow;
